import type { ReactNode } from 'react';
import { cn } from '../../utils/cn';

export interface BannerProps {
  /**
   * `danger` muda o que o backend faz (atuar como); `warning` só muda o que a
   * tela mostra (visualizar como).
   */
  tone: 'danger' | 'warning';
  children: ReactNode;
  /** Rótulo do botão de saída, à direita do texto. Sem ele, não há botão. */
  actionLabel?: string;
  onAction?: () => void;
  actionDisabled?: boolean;
}

const toneStyles: Record<BannerProps['tone'], { bar: string; action: string }> =
  {
    danger: {
      bar: 'bg-red-600 text-white',
      action: 'bg-white/20 hover:bg-white/30',
    },
    warning: {
      bar: 'bg-amber-500 text-amber-950',
      action: 'bg-amber-950/15 hover:bg-amber-950/25',
    },
  };

/**
 * Faixa de largura total no topo da página, para um estado da sessão que quem
 * usa precisa ver o tempo todo. `role="status"`: o leitor de tela anuncia
 * quando ela aparece, sem roubar o foco.
 */
export function Banner({
  tone,
  children,
  actionLabel,
  onAction,
  actionDisabled,
}: BannerProps) {
  return (
    <div
      role="status"
      className={cn(
        'flex items-center justify-center gap-3 px-4 py-2 text-center text-sm font-medium',
        toneStyles[tone].bar,
      )}
    >
      <span>{children}</span>
      {actionLabel && (
        <button
          type="button"
          onClick={onAction}
          disabled={actionDisabled}
          className={cn(
            'shrink-0 rounded-md px-2.5 py-1 text-xs font-semibold transition-colors disabled:opacity-50',
            toneStyles[tone].action,
          )}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}
